import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { LiquidationPrintModel } from 'src/app/models/print/subrental/liquidation-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SubrentalCutoffModel } from 'src/app/models/subrental/subrental-cutoff.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SubrentalCutoffService {

  private readonly _httpClient = inject(HttpClient);

  createSubrentalCutoff(subrentalCutoffContract: SubrentalCutoffModel): Observable<SubrentalCutoffModel> {
    return this._httpClient.post<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.CreateSubrentalCutoff}`,
      subrentalCutoffContract);
  }

  deleteSubrentalCutoff(subrentalCutoffId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.delete<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.DeleteSubrentalCutoff}/${subrentalCutoffId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportLiquidationErrorsTxt(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.ExportLiquidationErrorsTxt}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  exportSubrentalPreinvoices(subrentalCutoffId: number, storeId: number): Observable<Blob> {
    const params = new HttpParams().set('subrentalCutoffId', subrentalCutoffId).set('storeId', storeId);
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.ExportSubrentalPreinvoicesAsync}`,
      null, { params, responseType: 'blob' }
    );
  }

  getAllStoreForLiquidationSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetAllStoreForLiquidationSelectAsync}`);
  }

  getAllSubrentalCutoffsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetAllSubrentalCutoffsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetColumnsForGeneralConsultationAsync}`);
  }

  getEmptyLiquidationPrint(): Observable<Array<LiquidationPrintModel>> {
    return this._httpClient.get<Array<LiquidationPrintModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetEmptyLiquidationPrint}`);
  }

  getFirstSubrentalCutoffByKeysAsync(branchId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetFirstSubrentalCutoffByKeys}/${branchId}`
    );
  }

  getLastSubrentalCutoffByKeysAsync(branchId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetLastSubrentalCutoffByKeys}/${branchId}`
    );
  }

  getLiquidationCutoffByKeysAsync(branchId: number, consecutive: number, subrentalCutoffId: number | null, storeId: number | null): Observable<Array<LiquidationPrintModel>> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('consecutive', consecutive);
    if (subrentalCutoffId !== null) params = params.set('subrentalCutoffId', subrentalCutoffId);
    if (storeId !== null) params = params.set('storeId', storeId);
    return this._httpClient.get<Array<LiquidationPrintModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetLiquidationCutoffByKeysAsync}`,
      { params }
    );
  }

  getNextSubrentalCutoffByKeysAsync(branchId: number, consecutive: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetNextSubrentalCutoffByKeys}/${branchId}/${consecutive}`
    );
  }

  getPreviusSubrentalCutoffByKeysAsync(branchId: number, consecutive: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetPreviusSubrentalCutoffByKeys}/${branchId}/${consecutive}`
    );
  }

  getSubrentalCutoffById(subrentalCutoffId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetSubrentalCutoffById}/${subrentalCutoffId}`);
  }

  getSubrentalCutoffByKeysAsync(branchId: number, consecutive: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.get<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.GetSubrentalCutoffByKeys}/${branchId}/${consecutive}`);
  }

  liquidationBySubrentalCutoffId(subrentalCutoffId: number): Observable<HttpEvent<LoadPreInvoicingModel>> {
    return this._httpClient.request(new HttpRequest('PUT', `${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.LiquidationBySubrentalCutoffId}/${subrentalCutoffId}`, null,
      {
        reportProgress: true,
        responseType: 'json'
      }));
  }

  openCloseSubrentalCutoff(subrentalCutoffId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.put<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.OpenCloseSubrentalCutoffAsync}/${subrentalCutoffId}`, null);
  }

  openSubrentalCutoffById(subrentalCutoffId: number): Observable<SubrentalCutoffModel> {
    return this._httpClient.put<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.OpenSubrentalCutoffByIdAsync}/${subrentalCutoffId}`, null);
  }

  updateSubrentalCutoff(subrentalCutoffContract: SubrentalCutoffModel): Observable<SubrentalCutoffModel> {
    return this._httpClient.put<SubrentalCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.UpdateSubrentalCutoff}`,
      subrentalCutoffContract);
  }

  validateCountSubrentalCutoffsOpen(branchId: number): Observable<Array<number>> {
    return this._httpClient.get<Array<number>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalCutoff.ValidateCountSubrentalCutoffsOpen}/${branchId}`);
  }

}
